import { useState } from "react";
import { useGrinders } from "../hooks/useGrinders";
import { api } from "../lib/api";
import type { Grinder, GrinderInput } from "../types";

// Numeric fields are kept as strings while editing and coerced on save.
const EMPTY = {
  name: "", brand: "", burr: "", burr_mm: "", rpm_min: "", rpm_max: "", url: "",
  acquired_on: "", retired_on: "", notes: "",
  grind_base: "", off_frozen: "", off_natural: "", off_small_bean: "", active: true,
};
type FormState = typeof EMPTY;

function toInput(f: FormState): GrinderInput {
  const s = (v: string) => (v.trim() === "" ? null : v.trim());
  const n = (v: string) => (v.trim() === "" ? null : Number(v));
  return {
    name: f.name.trim(),
    brand: s(f.brand),
    burr: s(f.burr),
    burr_mm: n(f.burr_mm),
    rpm_min: n(f.rpm_min),
    rpm_max: n(f.rpm_max),
    url: s(f.url),
    acquired_on: s(f.acquired_on),
    retired_on: s(f.retired_on),
    notes: s(f.notes),
    grind_base: n(f.grind_base),
    off_frozen: n(f.off_frozen),
    off_natural: n(f.off_natural),
    off_small_bean: n(f.off_small_bean),
    active: f.active,
  };
}

function fromGrinder(g: Grinder): FormState {
  const n = (v: number | null) => (v != null ? String(v) : "");
  return {
    name: g.name, brand: g.brand ?? "", burr: g.burr ?? "", burr_mm: n(g.burr_mm),
    rpm_min: n(g.rpm_min), rpm_max: n(g.rpm_max), url: g.url ?? "",
    acquired_on: g.acquired_on ?? "", retired_on: g.retired_on ?? "", notes: g.notes ?? "",
    grind_base: n(g.grind_base), off_frozen: n(g.off_frozen), off_natural: n(g.off_natural),
    off_small_bean: n(g.off_small_bean), active: g.active,
  };
}

// "start 11.5 · +1 frozen · -0.5 natural · +1 small bean" — the brew form's grind-suggestion inputs.
function heuristicLabel(g: Grinder): string | null {
  if (g.grind_base == null) return null;
  const signed = (v: number) => (v > 0 ? `+${v}` : `${v}`);
  const offs: [number | null, string][] = [[g.off_frozen, "frozen"], [g.off_natural, "natural"], [g.off_small_bean, "small bean"]];
  return [`start ${g.grind_base}`, ...offs.filter(([v]) => v).map(([v, l]) => `${signed(v as number)} ${l}`)].join(" · ");
}

// "800–1400 rpm", or "1950 rpm" for a fixed-speed grinder.
function rpmLabel(g: Grinder): string | null {
  if (g.rpm_min == null && g.rpm_max == null) return null;
  if (g.rpm_min == null || g.rpm_max == null || g.rpm_min === g.rpm_max) return `${g.rpm_min ?? g.rpm_max} rpm`;
  return `${g.rpm_min}–${g.rpm_max} rpm`;
}

export function Grinders() {
  const { data: grinders, refresh } = useGrinders();
  const [f, setF] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof FormState, v: string | boolean) => setF((cur) => ({ ...cur, [k]: v }));
  const resetForm = () => { setF(EMPTY); setEditingId(null); setError(null); };

  const onSave = async () => {
    const input = toInput(f);
    if (!input.name) { setError("name is required"); return; }
    setBusy(true); setError(null);
    try {
      if (editingId != null) await api.grinders.update(editingId, input);
      else await api.grinders.create(input);
      resetForm();
      refresh();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const onEdit = (g: Grinder) => { setF(fromGrinder(g)); setEditingId(g.id); setError(null); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const onDelete = async (g: Grinder) => {
    if (!confirm(`Delete "${g.name}"? Brews logged with it lose the grinder label — their grind numbers become uninterpretable. To retire a grinder, untick Active instead.`)) return;
    setBusy(true); setError(null);
    try {
      await api.grinders.remove(g.id);
      if (editingId === g.id) resetForm();
      refresh();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="eyebrow">Setup · Grinders</div>
        <h1 className="font-display text-3xl">Grinders</h1>
        <p className="text-sm text-paper-600 max-w-xl">
          Your grinder catalog. Grind settings only mean something on the grinder that made them — "8.8"
          on one is not "8.8" on another — so each brew records which grinder (and rpm) it was ground on.
          Retire a sold grinder by unticking Active; keep it so old brews stay readable.
        </p>
      </header>

      {error ? <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div> : null}

      {/* Add / edit form */}
      <div className="border border-paper-200 p-4 space-y-4 max-w-2xl">
        <div className="eyebrow">{editingId != null ? "Edit grinder" : "Add grinder"}</div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <label className="block col-span-2 sm:col-span-3">
            <span className="label">Name *</span>
            <input className="input" placeholder="Timemore Sculptor 078SSP" value={f.name} onChange={(e) => set("name", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Brand</span>
            <input className="input" placeholder="Timemore" value={f.brand} onChange={(e) => set("brand", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Burr</span>
            <input className="input" placeholder="SSP MP" value={f.burr} onChange={(e) => set("burr", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Burr mm</span>
            <input className="input tabular-nums" inputMode="decimal" placeholder="78" value={f.burr_mm} onChange={(e) => set("burr_mm", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">RPM min</span>
            <input className="input tabular-nums" inputMode="numeric" placeholder="800" value={f.rpm_min} onChange={(e) => set("rpm_min", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">RPM max</span>
            <input className="input tabular-nums" inputMode="numeric" placeholder="1400" value={f.rpm_max} onChange={(e) => set("rpm_max", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Acquired</span>
            <input className="input" type="date" value={f.acquired_on} onChange={(e) => set("acquired_on", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Retired</span>
            <input className="input" type="date" value={f.retired_on} onChange={(e) => set("retired_on", e.target.value)} />
          </label>
          <label className="block col-span-2 sm:col-span-3">
            <span className="label">Product URL</span>
            <input className="input" placeholder="https://…" value={f.url} onChange={(e) => set("url", e.target.value)} />
          </label>
          <div className="col-span-2 sm:col-span-3 text-xs text-paper-500 pt-1">
            Grind heuristic — the brew form suggests base + offsets. Base = washed coffee, fresh (not frozen) bag.
          </div>
          <label className="block">
            <span className="label">Base grind</span>
            <input className="input tabular-nums" inputMode="decimal" placeholder="11.5" value={f.grind_base} onChange={(e) => set("grind_base", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">+ Frozen</span>
            <input className="input tabular-nums" inputMode="decimal" placeholder="1" value={f.off_frozen} onChange={(e) => set("off_frozen", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">+ Natural</span>
            <input className="input tabular-nums" inputMode="decimal" placeholder="-0.5" value={f.off_natural} onChange={(e) => set("off_natural", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">+ Small bean</span>
            <input className="input tabular-nums" inputMode="decimal" placeholder="1" value={f.off_small_bean} onChange={(e) => set("off_small_bean", e.target.value)} />
          </label>
          <label className="block col-span-2 sm:col-span-3">
            <span className="label">Notes</span>
            <input className="input" placeholder="stepless, run at 800 rpm" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button className="btn-primary" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : editingId != null ? "Save changes" : "Add grinder"}
          </button>
          <label className="flex items-center gap-2 text-sm text-paper-600">
            <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} />
            Active (show in brew form)
          </label>
          {editingId != null || f.name ? (
            <button type="button" className="btn-ghost" onClick={resetForm} disabled={busy}>Cancel</button>
          ) : null}
        </div>
      </div>

      {/* Catalog list */}
      <ul className="border-t border-paper-200 max-w-2xl">
        {grinders && grinders.length === 0 ? (
          <li className="py-4 text-sm text-paper-500">No grinders yet — add your first above.</li>
        ) : null}
        {(grinders || []).map((g) => (
          <li key={g.id} className="flex flex-wrap items-center gap-3 py-3 border-b border-paper-200">
            <div className="min-w-0 flex-1 basis-48">
              <div className="flex items-center gap-2">
                <span className="text-[15px] text-paper-800">{g.name}</span>
                {!g.active ? <span className="text-[10px] uppercase tracking-widest text-paper-400">inactive</span> : null}
              </div>
              <div className="text-xs text-paper-500">
                {[g.burr, g.burr_mm != null ? `${g.burr_mm} mm` : null, rpmLabel(g)].filter(Boolean).join(" · ") || "—"}
                {g.acquired_on || g.retired_on ? ` · ${g.acquired_on ?? "?"} → ${g.retired_on ?? "now"}` : ""}
                {g.notes ? ` · ${g.notes}` : ""}
              </div>
              {heuristicLabel(g) ? <div className="text-xs text-paper-500">{heuristicLabel(g)}</div> : null}
            </div>
            <button className="text-[10px] uppercase tracking-widest text-paper-500 hover:text-ink" onClick={() => onEdit(g)}>Edit</button>
            <button className="text-[10px] uppercase tracking-widest text-paper-400 hover:text-red-700" onClick={() => onDelete(g)} disabled={busy}>Delete</button>
          </li>
        ))}
      </ul>
    </div>
  );
}
